// ViewToolbarFilterPills.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
    MAX_VISIBLE_FILTER_PILLS,
    buildActiveFacetPills,
} from 'views/utils/buildActiveFacetPills';

// CSS
import './ViewToolbarFilterPills.css';

// Icons
import Icon from 'svg/Icon';

/**
 * ViewToolbarFilterPills
 *
 * Desktop chips for active lens filters. The pill body opens the filter menu;
 * the close control clears that facet.
 *
 * @param {Array} facetFields - Schema-backed lens field definitions.
 * @param {object} facetSelections - Selected value by field key.
 * @param {boolean} [isBoardMode=false] - Whether Board group-by is active.
 * @param {string|null} [boardFieldKey=null] - Active Board group field.
 * @param {number} [maxVisible] - Visible pill cap before +N overflow.
 * @param {function} onClear - Called with a field key to clear.
 * @param {function} onOpenMenu - Opens the parent filter menu.
 * @returns {ReactNode|null}
 */
const ViewToolbarFilterPills = React.memo(({
    facetFields = [],
    facetSelections = {},
    isBoardMode = false,
    boardFieldKey = null,
    maxVisible = MAX_VISIBLE_FILTER_PILLS,
    onClear,
    onOpenMenu,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- MEMOS ----- //

    const pills = useMemo(() => (
        buildActiveFacetPills(facetFields, facetSelections, {
            isBoardMode,
            boardFieldKey,
        })
    ), [boardFieldKey, facetFields, facetSelections, isBoardMode]);

    const visibleCount = Math.max(0, Number(maxVisible) || MAX_VISIBLE_FILTER_PILLS);
    const visiblePills = pills.slice(0, visibleCount);
    const hiddenCount = Math.max(0, pills.length - visiblePills.length);

    if (!pills.length) return null;

    // ----- HANDLERS ----- //

    /**
     * handleClear
     *
     * Clears one active facet without opening the filter menu.
     *
     * @param {object} event
     * @param {string} fieldKey
     */
    const handleClear = (event, fieldKey) => {
        event.preventDefault();
        event.stopPropagation();
        onClear?.(fieldKey);
    };

    // ----- RENDER ----- //

    return (
        <div className="view-toolbar-filter-pills-container">
            {visiblePills.map((pill) => (
                <div
                    key={pill.fieldKey}
                    className="view-toolbar-filter-pill"
                    title={pill.label}
                >

                    {/* LABEL */}
                    <button
                        className="view-toolbar-filter-pill-label"
                        type="button"
                        onClick={onOpenMenu}
                    >
                        {pill.valueLabel}
                    </button>

                    {/* CLEAR */}
                    <button
                        className="view-toolbar-filter-pill-clear"
                        type="button"
                        title={`Clear ${pill.fieldLabel}`}
                        aria-label={`Clear ${pill.fieldLabel}`}
                        onClick={(event) => handleClear(event, pill.fieldKey)}
                    >
                        <Icon
                            iconKey="close"
                            width={12}
                            height={12}
                            color={theme.foregroundMuted}
                        />
                    </button>
                </div>
            ))}

            {hiddenCount > 0 ? (
                <button
                    className="view-toolbar-filter-pill-overflow"
                    type="button"
                    title={`${hiddenCount} more filters`}
                    onClick={onOpenMenu}
                >
                    +{hiddenCount}
                </button>
            ) : null}
        </div>
    );
});

// Dev Tools
ViewToolbarFilterPills.displayName = 'ViewToolbarFilterPills';

export default ViewToolbarFilterPills;
